<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";

import actions from "~/actions";
import Avatar from "~/components/dumb/Avatar.vue";
import { USER_KIND_USER } from "~/constants/userKinds";
import { PlusIcon } from "~/icons";
import { makeLinkToSettingsRef } from "~/router/common";
import { UserRole } from "~/shared/enums";
import type { User } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import { getUserDisplayName } from "~/utils/user";

const router = useRouter();
const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();

const inviteUsers = () => router.push(makeLinkToSettingsRef("teammates").value);

const users = computed(() => dataStore.getUserList());

const getContextMenuSections = (user: User) =>
  actions.context.user(
    user,
    async (eventKind: string) => {
      switch (eventKind) {
        case "openUserPage": {
          if (user.kind !== USER_KIND_USER) {
            router.push(makeLinkToSettingsRef("agents").value);
          } else {
            router.push(makeLinkToSettingsRef("teammates").value);
          }
          break;
        }
        default: {
          throw new Error(`Unknown event kind: ${eventKind}`);
        }
      }
    },
    true
  );

const onContextMenu = (event: MouseEvent, user: User) => {
  if (tenantStore.isDart && !pageStore.adminHidden && event.altKey) {
    return;
  }
  appStore.openContextMenu(event as PointerEvent, getContextMenuSections(user));
};
</script>

<template>
  <div class="h-80 w-full rounded-xl py-4 pl-4 bg-lt">
    <div class="flex max-h-full flex-wrap gap-5 overflow-y-scroll pr-px">
      <!-- Add button -->
      <div
        v-if="userStore.isRoleGte(UserRole.MEMBER)"
        class="flex cursor-pointer flex-col items-center gap-2 rounded-lg p-3 hover:bg-md"
        @click="inviteUsers"
        @keydown.enter="inviteUsers">
        <div class="flex size-20 items-center justify-center rounded-full border-2 border-hvy">
          <PlusIcon class="text-md icon-md" />
        </div>
        <span class="select-none text-xs font-semibold text-lt">Invite</span>
      </div>

      <!-- Users -->
      <div
        v-for="user in users"
        :key="user.duid"
        class="flex flex-col items-center gap-2 p-3"
        @contextmenu="(event: MouseEvent) => onContextMenu(event, user)">
        <Avatar :user="user" class="size-20" />
        <span :title="getUserDisplayName(user)" class="max-w-20 select-none truncate text-xs font-semibold text-md">
          {{ getUserDisplayName(user) }}
        </span>
      </div>
    </div>
  </div>
</template>
